import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Ban, Undo2, RotateCcw, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusChip } from "./AddressDatabase";

const card = "rounded-2xl bg-white border border-slate-200 p-5";
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const EVENT_LABELS = {
  imported: "Imported", assigned: "Assigned to campaign", campaign_assigned: "Campaign assignment",
  campaign_group_linked: "Linked to campaign group", letter_generated: "Letter generated",
  submitted_to_mail: "Submitted to Bing Mail", mailed: "Mailed", qr_scanned: "QR scanned",
  form_completed: "Eligibility form completed", lead_created: "Lead created",
  booking_created: "Booking created", inspection_completed: "Inspection completed",
  water_test_created: "Water test created", do_not_mail_set: "Do Not Mail set",
  do_not_mail_removed: "Do Not Mail removed",
  test_activity_reset: "Test activity reset",
};

const DNM_REASONS = [["customer_requested", "Customer requested no mail"], ["existing_customer", "Existing customer"],
  ["tenant_ineligible", "Tenant / ineligible"], ["invalid_address", "Invalid address"], ["duplicate", "Duplicate"],
  ["unsuitable_property", "Unsuitable property"], ["business_exclusion", "Business/internal exclusion"],
  ["complaint", "Complaint"], ["other", "Other"]];

export default function MasterPropertyDetail() {
  const { pid } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dnmReason, setDnmReason] = useState("customer_requested");
  const [resetAudit, setResetAudit] = useState(null);
  const [resetConfirm, setResetConfirm] = useState("");

  const load = useCallback(() => {
    api.get(`/admin/address-database/${pid}`).then(({ data: d }) => setData(d)).catch(() => toast.error("Property not found"));
  }, [pid]);
  useEffect(load, [load]);

  if (!data) return <div className="p-10 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const p = data.property;

  const setDnm = async (remove) => {
    if (!remove && !window.confirm("Mark this property Do Not Mail? It will be permanently blocked from campaign selection, letter generation and mail submission until an admin removes the suppression.")) return;
    setBusy(true);
    try {
      await api.post(`/admin/address-database/${pid}/do-not-mail`, remove ? { remove: true } : { reason: dnmReason });
      toast.success(remove ? "Suppression removed" : "Do Not Mail set");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openResetAudit = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/address-database/${pid}/reset-audit`);
      setResetAudit(data); setResetConfirm("");
      if (!data.removable.length) toast.info("No test activity found on this property");
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/address-database/${pid}/reset-test-activity`, { confirm_text: resetConfirm });
      toast.success(`Test activity removed — property restored to "${data.restored_status.replaceAll("_", " ")}"`);
      setResetAudit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  const resolveConflict = async (resolution) => {
    setBusy(true);
    try {
      await api.post(`/admin/address-database/${pid}/resolve-conflict`, { resolution });
      toast.success("Conflict resolved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="master-property-detail">
      <Link to="/admin/address-database" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Address Database</Link>
      <div className="flex flex-wrap items-start justify-between gap-3 mt-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{p.normalized_address}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            <StatusChip status={p.status} />
            {p.campaign_group && <span className="font-mono text-xs bg-slate-100 rounded px-2 py-0.5">{p.campaign_group}</span>}
            {p.water_confidence && <span>Water confidence: <b>{p.water_confidence}</b></span>}
            <span>Mailed {p.times_mailed || 0}×{p.last_mailed_at ? ` (last ${fmtDT(p.last_mailed_at)})` : ""}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={openResetAudit} className="rounded-full border-amber-300 text-amber-800" data-testid="mp-reset-btn">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset Test Activity
          </Button>
          {p.do_not_mail ? (
            <Button variant="outline" disabled={busy} onClick={() => setDnm(true)} className="rounded-full border-red-300 text-red-700" data-testid="mp-dnm-remove">
              <Undo2 className="w-4 h-4 mr-1" /> Remove Do Not Mail
            </Button>
          ) : (
            <div className="flex items-center gap-1.5">
              <select className="rounded-lg border border-slate-300 text-xs px-2 py-2 bg-white" value={dnmReason} onChange={(e) => setDnmReason(e.target.value)} data-testid="mp-dnm-reason">
                {DNM_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <Button variant="outline" disabled={busy} onClick={() => setDnm(false)} className="rounded-full border-red-300 text-red-700" data-testid="mp-dnm-set">
                <Ban className="w-4 h-4 mr-1" /> Do Not Mail
              </Button>
            </div>
          )}
        </div>
      </div>

      {p.do_not_mail && (
        <div className="mt-4 rounded-2xl bg-red-50 border border-red-300 p-4 text-sm text-red-900" data-testid="mp-dnm-banner">
          <b>Do Not Mail</b> — {DNM_REASONS.find(([v]) => v === p.do_not_mail_reason)?.[1] || p.do_not_mail_reason} · set by {p.do_not_mail_set_by} on {fmtDT(p.do_not_mail_set_at)}. Blocked from campaign selection, letters and mail submission.
        </div>
      )}
      {(p.conflict || {}).status === "unresolved" && (
        <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-300 p-4 text-sm text-amber-900" data-testid="mp-conflict-banner">
          <b>Possible address conflict:</b> {p.conflict.detail || p.conflict.existing_address}
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveConflict("keep_both")}>Keep both</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => resolveConflict("excluded")}>Exclude this property</Button>
          </div>
        </div>
      )}

      {resetAudit && resetAudit.removable.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setResetAudit(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mp-reset-dialog">
            <h3 className="font-bold text-slate-900 text-lg">Reset Test Activity — Dry Run</h3>
            <p className="text-xs text-slate-500 mt-1">Removes ONLY accidental test interactions for <b>{resetAudit.address}</b>. Nothing has been removed yet.</p>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-red-700">Will be removed</div>
              <div className="mt-1.5 space-y-1" data-testid="mp-reset-removable">
                {resetAudit.removable.map((r, i) => (
                  <div key={i} className="flex justify-between text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-1.5">
                    <span>{r.label}</span><span className="font-semibold">{r.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Preserved (untouched)</div>
              <div className="mt-1.5 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 space-y-0.5" data-testid="mp-reset-preserved">
                <div>Campaign assignment: <b className="font-mono">{resetAudit.preserved.campaign_assignment || "—"}</b></div>
                <div>QR token(s): <b className="font-mono">{resetAudit.preserved.qr_tokens.filter(Boolean).join(", ") || "—"}</b> — QR stays valid for the real resident</div>
                <div>Mailed status, letter refs, Bing Mail history, recipient record, mail audit history</div>
              </div>
            </div>

            {resetAudit.warnings.length > 0 && (
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900" data-testid="mp-reset-warnings">
                {resetAudit.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}

            {resetAudit.blockers.length > 0 ? (
              <div className="mt-3 rounded-lg bg-red-100 border border-red-400 px-3 py-2" data-testid="mp-reset-blockers">
                <div className="text-sm font-bold text-red-800 flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Reset blocked — shared live records</div>
                <ul className="mt-1 text-xs text-red-800 list-disc pl-4">
                  {resetAudit.blockers.map((b, i) => <li key={i}>{b.detail}</li>)}
                </ul>
              </div>
            ) : (
              <div className="mt-4">
                <div className="text-xs text-slate-600">Property will return to <b>{resetAudit.restored_status.replaceAll("_", " ")}</b> (fresh mailed-recipient state). Type <b className="font-mono">{resetAudit.confirm_phrase}</b> to confirm:</div>
                <input className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)} placeholder={resetAudit.confirm_phrase} data-testid="mp-reset-confirm-input" />
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setResetAudit(null)}>Cancel</Button>
              {resetAudit.resettable && (
                <Button disabled={busy || resetConfirm !== resetAudit.confirm_phrase} onClick={doReset}
                  className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="mp-reset-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove Test Activity"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`${card} mt-5`} data-testid="mp-source">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Source traceability</div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><div className="text-xs text-slate-400">Source street</div>{p.source_street || "—"}</div>
          <div><div className="text-xs text-slate-400">Source file</div>{p.source_file || (p.entry_source === "manual" ? "Manual entry" : "—")}</div>
          <div><div className="text-xs text-slate-400">Import batch</div><span className="font-mono text-xs">{p.source_import_batch_id ? p.source_import_batch_id.slice(0, 8) : "—"}</span></div>
          <div><div className="text-xs text-slate-400">First imported</div>{fmtDT(p.first_imported_at)}</div>
        </div>
      </div>

      {data.assignments.length > 0 && (
        <div className={`${card} mt-5`} data-testid="mp-assignments">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Campaign history</div>
          <table className="w-full text-sm mt-2">
            <tbody>
              {data.assignments.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5 font-mono text-xs font-semibold">{a.campaign_group}</td>
                  <td className="py-1.5 text-slate-500 text-xs">{a.source}</td>
                  <td className="py-1.5 text-slate-500 text-xs">{fmtDT(a.assigned_at)} · {a.assigned_by}</td>
                  <td className="py-1.5 text-right">
                    {a.campaign_id && <Link to={`/admin/property-campaigns/${a.campaign_id}`} className="text-cyan-700 text-xs font-semibold">Open campaign →</Link>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className={`${card} mt-5`} data-testid="mp-timeline">
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Property timeline</div>
        <div className="mt-3 space-y-0">
          {data.timeline.map((e, i) => (
            <div key={i} className="flex gap-3 pb-3 relative">
              <div className="flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full mt-1 ${["lead_created", "booking_created", "inspection_completed", "water_test_created"].includes(e.event) ? "bg-emerald-600" : e.event.startsWith("do_not_mail") ? "bg-red-500" : "bg-cyan-600"}`} />
                {i < data.timeline.length - 1 && <div className="w-px flex-1 bg-slate-200" />}
              </div>
              <div className="text-sm">
                <span className="font-semibold text-slate-900">{EVENT_LABELS[e.event] || e.event}</span>
                {e.detail && <span className="text-slate-500"> — {e.detail}</span>}
                {e.reference && <span className="font-mono text-[10px] text-slate-400 ml-1">{e.reference}</span>}
                <div className="text-xs text-slate-400">{fmtDT(e.at)}{e.actor ? ` · ${e.actor}` : ""}</div>
              </div>
            </div>
          ))}
          {!data.timeline.length && <div className="text-sm text-slate-400">No events yet</div>}
        </div>
      </div>

      {data.audit.length > 0 && (
        <div className={`${card} mt-5`} data-testid="mp-audit">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Audit history</div>
          <div className="mt-2 space-y-1.5 text-xs text-slate-600">
            {data.audit.map((a) => (
              <div key={a.id}>{fmtDT(a.at)} · <b>{a.action}</b> by {a.actor}{a.detail ? ` — ${a.detail}` : ""}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
